"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useSearchParams } from "next/navigation"
import * as signalR from "@microsoft/signalr"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import {
  Plus,
  Users,
  ThumbsUp,
  Trash2,
  SkipForward,
  Copy,
  Check,
  MessageSquare,
  Vote,
  ClipboardList,
  Link as LinkIcon
} from "lucide-react"
import { toast } from "sonner"
import { API_URL } from "@/lib/api-config"

// Types matching backend
interface RetroCard {
  id: string
  content: string
  authorName: string
  authorConnectionId: string
  votes: number
  voterConnectionIds: string[]
  createdAt: string
}

interface RetroColumn {
  id: string
  name: string
  order: number
  cards: RetroCard[]
}

interface RetroParticipant {
  connectionId: string
  name: string
  joinedAt: string
}

interface RetroBoard {
  id: string
  title: string
  ownerName: string
  columns: RetroColumn[]
  participants: RetroParticipant[]
  phase: RetroBoardPhase
  createdAt: string
}

enum RetroBoardPhase {
  AddCards = 0,
  Vote = 1,
  Discuss = 2,
  ActionItems = 3
}

type ViewState = "landing" | "create" | "join" | "board"

const defaultColumns = [
  "What went well",
  "What to improve",
  "Action items"
]

const phaseLabels: Record<RetroBoardPhase, { label: string; icon: React.ReactNode; description: string }> = {
  [RetroBoardPhase.AddCards]: {
    label: "Add Cards",
    icon: <Plus className="h-4 w-4" />,
    description: "Team members add their thoughts to each column"
  },
  [RetroBoardPhase.Vote]: {
    label: "Vote",
    icon: <Vote className="h-4 w-4" />,
    description: "Vote on the most important items to discuss"
  },
  [RetroBoardPhase.Discuss]: {
    label: "Discuss",
    icon: <MessageSquare className="h-4 w-4" />,
    description: "Discuss top-voted items as a team"
  },
  [RetroBoardPhase.ActionItems]: {
    label: "Action Items",
    icon: <ClipboardList className="h-4 w-4" />,
    description: "Define action items and owners"
  }
}

export function RetrospectiveBoard() {
  const searchParams = useSearchParams()
  const [viewState, setViewState] = useState<ViewState>("landing")
  const [board, setBoard] = useState<RetroBoard | null>(null)
  const [userName, setUserName] = useState("")
  const [boardTitle, setBoardTitle] = useState("Sprint Retrospective")
  const [boardId, setBoardId] = useState("")
  const [newCardContent, setNewCardContent] = useState<Record<string, string>>({})
  const [copied, setCopied] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)

  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const [myConnectionId, setMyConnectionId] = useState("")

  // Check for board ID in URL to auto-fill join form
  useEffect(() => {
    const boardParam = searchParams.get('board')
    if (!boardParam) return

    let active = true
    queueMicrotask(() => {
      if (!active) return
      setBoardId(boardParam.toUpperCase())
      setViewState("join")
    })
    return () => {
      active = false
    }
  }, [searchParams])

  const setupConnection = useCallback(async () => {
    if (connectionRef.current) {
      await connectionRef.current.stop()
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${API_URL}/hubs/retro`)
      .withAutomaticReconnect()
      .build()

    // Event handlers
    connection.on("UserJoined", (participant: RetroParticipant) => {
      setBoard(prev => {
        if (!prev) return prev
        const existing = prev.participants.find(p => p.connectionId === participant.connectionId)
        if (existing) return prev
        return {
          ...prev,
          participants: [...prev.participants, participant]
        }
      })
      toast.success(`${participant.name} joined the board`)
    })

    connection.on("UserLeft", (name: string) => {
      setBoard(prev => {
        if (!prev) return prev
        return {
          ...prev,
          participants: prev.participants.filter(p => p.name !== name)
        }
      })
      toast.info(`${name} left the board`)
    })

    connection.on("CardAdded", (columnId: string, card: RetroCard) => {
      setBoard(prev => {
        if (!prev) return prev
        return {
          ...prev,
          columns: prev.columns.map(col =>
            col.id === columnId
              ? { ...col, cards: [...col.cards, card] }
              : col
          )
        }
      })
    })

    connection.on("CardUpdated", (columnId: string, card: RetroCard) => {
      setBoard(prev => {
        if (!prev) return prev
        return {
          ...prev,
          columns: prev.columns.map(col =>
            col.id === columnId
              ? { ...col, cards: col.cards.map(c => c.id === card.id ? card : c) }
              : col
          )
        }
      })
    })

    connection.on("CardDeleted", (columnId: string, cardId: string) => {
      setBoard(prev => {
        if (!prev) return prev
        return {
          ...prev,
          columns: prev.columns.map(col =>
            col.id === columnId
              ? { ...col, cards: col.cards.filter(c => c.id !== cardId) }
              : col
          )
        }
      })
    })

    connection.on("CardVoted", (columnId: string, cardId: string, votes: number) => {
      setBoard(prev => {
        if (!prev) return prev
        return {
          ...prev,
          columns: prev.columns.map(col =>
            col.id === columnId
              ? {
                ...col,
                cards: col.cards.map(c =>
                  c.id === cardId ? { ...c, votes } : c
                )
              }
              : col
          )
        }
      })
    })

    connection.on("PhaseChanged", (phase: RetroBoardPhase) => {
      setBoard(prev => prev ? { ...prev, phase } : prev)
      toast.info(`Phase changed to: ${phaseLabels[phase].label}`)
    })

    connectionRef.current = connection
    return connection
  }, [])

  const createBoard = async () => {
    if (!userName.trim()) {
      toast.error("Please enter your name")
      return
    }

    setIsConnecting(true)
    try {
      const connection = await setupConnection()
      await connection.start()
      setMyConnectionId(connection.connectionId || "")

      const newBoard = await connection.invoke<RetroBoard>(
        "CreateBoard",
        boardTitle,
        userName,
        defaultColumns
      )

      setBoard(newBoard)
      setViewState("board")
      toast.success(`Board "${newBoard.title}" created!`)
    } catch (err) {
      toast.error("Failed to create board")
      console.error(err)
    } finally {
      setIsConnecting(false)
    }
  }

  const joinBoard = async () => {
    if (!userName.trim()) {
      toast.error("Please enter your name")
      return
    }
    if (!boardId.trim()) {
      toast.error("Please enter the board ID")
      return
    }

    setIsConnecting(true)
    try {
      const connection = await setupConnection()
      await connection.start()
      setMyConnectionId(connection.connectionId || "")

      const existingBoard = await connection.invoke<RetroBoard | null>(
        "JoinBoard",
        boardId.toUpperCase(),
        userName
      )

      if (!existingBoard) {
        toast.error("Board not found")
        await connection.stop()
        return
      }

      setBoard(existingBoard)
      setViewState("board")
      toast.success(`Joined "${existingBoard.title}"!`)
    } catch (err) {
      toast.error("Failed to join board")
      console.error(err)
    } finally {
      setIsConnecting(false)
    }
  }

  const addCard = async (columnId: string) => {
    const content = newCardContent[columnId]?.trim()
    if (!content || !connectionRef.current) return

    try {
      await connectionRef.current.invoke("AddCard", columnId, content, userName)
      setNewCardContent(prev => ({ ...prev, [columnId]: "" }))
    } catch {
      toast.error("Failed to add card")
    }
  }

  const deleteCard = async (cardId: string) => {
    if (!connectionRef.current) return
    try {
      await connectionRef.current.invoke("DeleteCard", cardId)
    } catch {
      toast.error("Failed to delete card")
    }
  }

  const voteCard = async (cardId: string, hasVoted: boolean) => {
    if (!connectionRef.current) return
    try {
      if (hasVoted) {
        await connectionRef.current.invoke("UnvoteCard", cardId)
      } else {
        await connectionRef.current.invoke("VoteCard", cardId)
      }
    } catch {
      toast.error("Failed to vote")
    }
  }

  const setPhase = async (phase: RetroBoardPhase) => {
    if (!connectionRef.current) return
    try {
      await connectionRef.current.invoke("SetPhase", phase)
    } catch {
      toast.error("Failed to change phase")
    }
  }

  const copyBoardId = () => {
    if (board) {
      navigator.clipboard.writeText(board.id)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success("Board ID copied!")
    }
  }

  const copyShareableLink = () => {
    if (board) {
      const url = `${window.location.origin}/agile/retrospective?board=${board.id}`
      navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success("Link copied! Share it with your team")
    }
  }

  const leaveBoard = async () => {
    if (connectionRef.current) {
      await connectionRef.current.stop()
    }
    setBoard(null)
    setViewState("landing")
  }

  useEffect(() => {
    return () => {
      connectionRef.current?.stop()
    }
  }, [])

  // Landing view
  if (viewState === "landing") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Retrospective Board
          </CardTitle>
          <CardDescription>
            Run effective sprint retrospectives with your team in real-time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Button
              onClick={() => setViewState("create")}
              className="h-24 flex flex-col gap-2"
            >
              <Plus className="h-6 w-6" />
              <span>Create New Board</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setViewState("join")}
              className="h-24 flex flex-col gap-2"
            >
              <Users className="h-6 w-6" />
              <span>Join Existing Board</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Create view
  if (viewState === "create") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Create Retrospective Board</CardTitle>
          <CardDescription>Start a new retrospective session</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="your-name">Your Name</Label>
            <Input
              id="your-name"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Enter your name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="board-title">Board Title</Label>
            <Input
              id="board-title"
              value={boardTitle}
              onChange={(e) => setBoardTitle(e.target.value)}
              placeholder="Sprint Retrospective"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setViewState("landing")}>
              Back
            </Button>
            <Button onClick={createBoard} disabled={isConnecting}>
              {isConnecting ? "Creating..." : "Create Board"}
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Join view
  if (viewState === "join") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Join Retrospective Board</CardTitle>
          <CardDescription>Enter the board ID to join</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="your-name-join">Your Name</Label>
            <Input
              id="your-name-join"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Enter your name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="board-id">Board ID</Label>
            <Input
              id="board-id"
              value={boardId}
              onChange={(e) => setBoardId(e.target.value.toUpperCase())}
              placeholder="ABC123"
              className="uppercase"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setViewState("landing")}>
              Back
            </Button>
            <Button onClick={joinBoard} disabled={isConnecting}>
              {isConnecting ? "Joining..." : "Join Board"}
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Board view
  if (!board) return null

  const isOwner = board.ownerName === userName
  const currentPhase = board.phase

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold">{board.title}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className="cursor-pointer" onClick={copyBoardId}>
                  {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                  {board.id}
                </Badge>
                <Badge variant="outline">
                  <Users className="h-3 w-3 mr-1" />
                  {board.participants.length} participants
                </Badge>
              </div>
            </div>

            {/* Phase controls */}
            <div className="flex items-center gap-2">
              <div className="text-sm text-muted-foreground mr-2">
                {phaseLabels[currentPhase].icon}
                <span className="ml-1">{phaseLabels[currentPhase].label}</span>
              </div>
              {isOwner && currentPhase < RetroBoardPhase.ActionItems && (
                <Button
                  size="sm"
                  onClick={() => setPhase(currentPhase + 1)}
                >
                  <SkipForward className="h-4 w-4 mr-1" />
                  Next Phase
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={copyShareableLink}>
                {copied ? <Check className="h-4 w-4 mr-1" /> : <LinkIcon className="h-4 w-4 mr-1" />}
                {copied ? "Copied!" : "Invite"}
              </Button>
              <Button variant="outline" size="sm" onClick={leaveBoard}>
                Leave
              </Button>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mt-2">
            {phaseLabels[currentPhase].description}
          </p>
        </CardContent>
      </Card>

      {/* Columns */}
      <div className="grid gap-4 md:grid-cols-3">
        {board.columns.map((column) => (
          <Card key={column.id} className="flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{column.name}</CardTitle>
              <CardDescription>{column.cards.length} cards</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 space-y-3">
              {/* Add card form - only in AddCards phase */}
              {currentPhase === RetroBoardPhase.AddCards && (
                <div className="space-y-2">
                  <Textarea
                    placeholder="Add a card..."
                    value={newCardContent[column.id] || ""}
                    onChange={(e) => setNewCardContent(prev => ({
                      ...prev,
                      [column.id]: e.target.value
                    }))}
                    className="min-h-[60px]"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.metaKey) {
                        addCard(column.id)
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={() => addCard(column.id)}
                    disabled={!newCardContent[column.id]?.trim()}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Card
                  </Button>
                </div>
              )}

              {/* Cards */}
              <div className="space-y-2">
                {[...column.cards]
                  .sort((a, b) => currentPhase >= RetroBoardPhase.Vote ? b.votes - a.votes : 0)
                  .map((card) => {
                    const isMyCard = card.authorConnectionId === myConnectionId
                    const hasVoted = card.voterConnectionIds?.includes(myConnectionId)

                    return (
                      <div
                        key={card.id}
                        className={`p-3 border rounded-lg bg-card ${isMyCard ? "border-primary/50" : ""}`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{card.content}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-muted-foreground">
                            {card.authorName}
                          </span>
                          <div className="flex items-center gap-1">
                            {currentPhase === RetroBoardPhase.Vote && (
                              <Button
                                variant={hasVoted ? "default" : "ghost"}
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => voteCard(card.id, hasVoted)}
                              >
                                <ThumbsUp className="h-3 w-3 mr-1" />
                                {card.votes}
                              </Button>
                            )}
                            {currentPhase >= RetroBoardPhase.Vote && card.votes > 0 && currentPhase !== RetroBoardPhase.Vote && (
                              <Badge variant="secondary" className="h-6">
                                <ThumbsUp className="h-3 w-3 mr-1" />
                                {card.votes}
                              </Badge>
                            )}
                            {isMyCard && currentPhase === RetroBoardPhase.AddCards && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-destructive hover:text-destructive"
                                onClick={() => deleteCard(card.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Participants */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Participants</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {board.participants.map((p) => (
              <Badge key={p.connectionId} variant={p.name === board.ownerName ? "default" : "secondary"}>
                {p.name}
                {p.name === board.ownerName && " (host)"}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
